import { useState } from 'react';
import { useRequestPasswordChange } from '@/api/hooks/useProfile';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/utils/errorHandler';
import { toast } from 'sonner';
import { LockClosedIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

export function PasswordSection() {
    const requestPasswordChange = useRequestPasswordChange();
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [emailSent, setEmailSent] = useState(false);

    async function handleRequestChange() {
        try {
            await requestPasswordChange.mutateAsync();
            setEmailSent(true);
            setShowConfirmation(false);
            toast.success('Password reset email sent');
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    }

    return (
        <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-secondary">
            <div className="flex items-center gap-3">
                <div className="rounded-lg bg-gray-100 p-2 dark:bg-dark">
                    <LockClosedIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Password</h2>
            </div>

            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                Change your password by requesting a secure reset link sent to your email.
            </p>

            <div className="mt-4">
                {emailSent ? (
                    <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
                        <CheckCircleIcon className="mt-0.5 h-5 w-5 text-green-600 dark:text-green-400" />
                        <div>
                            <p className="font-medium text-green-800 dark:text-green-300">
                                Check your email
                            </p>
                            <p className="mt-1 text-sm text-green-700 dark:text-green-400">
                                We've sent a password reset link to your email address. The link will expire in 1 hour for security.
                            </p>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEmailSent(false)}
                                className="mt-2 text-green-700 hover:bg-green-100 dark:text-green-400 dark:hover:bg-green-900/30"
                            >
                                Request another link
                            </Button>
                        </div>
                    </div>
                ) : showConfirmation ? (
                    <div className="space-y-4">
                        <p className="text-gray-600 dark:text-gray-400">
                            We'll send a password reset link to your email address. Click the link to set a new password.
                            The link expires in 1 hour.
                        </p>
                        <div className="flex gap-3">
                            <Button
                                onClick={handleRequestChange}
                                isLoading={requestPasswordChange.isPending}
                                disabled={requestPasswordChange.isPending}
                            >
                                Send Reset Link
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={() => setShowConfirmation(false)}
                                disabled={requestPasswordChange.isPending}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-between">
                        <p className="text-gray-600 dark:text-gray-400">
                            ••••••••••••
                        </p>
                        <Button variant="secondary" size="sm" onClick={() => setShowConfirmation(true)}>
                            Change Password
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
